/* eslint-disable @typescript-eslint/member-ordering */
/**
 * @author zhaolongfei
 */

import boxx from '@baidu/boxx-fe';
import {debounce} from '@lib/utils/descriptor';
import {REGEXP_HTML, REGEXP_HTML_G} from '../constants';
import __INSTANCE__ from '../InstanceManager';
import {isMockId} from './tools';

// 滚动到页面底部
export const scrollToContainerBottom = ($container = __INSTANCE__.config.$scroller) => {
    $container.scrollTo(0, $container.scrollHeight);
};
export const scrollToContainerBottomSmooth = ($container = __INSTANCE__.config.$scroller) => {
    $container.scrollTo({
        left: 0,
        top: $container.scrollHeight,
        behavior: 'smooth',
    });
};

// 循环滚动到页面底部
export const loopScroll = () => {
    let start: any = null;
    const duration = 300;
    const loop = () => {
        if (!start) {
            start = performance.now();
        }
        const elapsed = performance.now() - start;
        window.scroll(0, document.documentElement.scrollHeight);
        // 如果没有达到300ms，则继续请求下一帧
        if (elapsed < duration) {
            requestAnimationFrame(loop);
        }
    };

    loop();
};

export const isSupportResizeObserver = () => {
    const {OSVersion, versionCompare, isIOS, isAndroid} = boxx.env;
    const isSupportResizeObserver = isAndroid || (isIOS && versionCompare(OSVersion, '13.1') >= 0);
    return isSupportResizeObserver;
};

export const onDomResize = (dom: Element, cb: () => void) => {
    let observer = null;

    if (isSupportResizeObserver()) {
        observer = new ResizeObserver(cb);
        observer.observe(dom);
    } else {
        // 兼容处理 iOS13.0 不支持 ResizeObserver
        observer = new MutationObserver(cb);
        observer.observe(dom, {
            childList: true,
            subtree: true,
        });
    }

    return observer;
};

interface onDomVisibleParams {
    dom: Element;
    cb: () => void;
    options?: object;
}
export const onDomVisible = (params: onDomVisibleParams) => {
    const defaultOptions = {
        root: null,
        rootMargin: '0px',
        threshold: [0],
    };
    const {dom, cb, options = defaultOptions} = params;

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                cb();
                observer.unobserve(entry.target);
            }
        });
    }, options);
    observer.observe(dom);
    return observer;
};

export const isPureTextMessage = (messageId: string) => {
    const msgIdContainer = document.getElementById(messageId);
    const container: Element | null = msgIdContainer?.querySelector('.can-all-select') ?? null;
    if (!container) {
        return false;
    }
    if (msgIdContainer?.querySelector('.ttv-plugin, .lui-card')) {
        return false;
    }
    const filter = [
        'PRE',
        'TABLE',
        'IMG',
        '.katex',
        '.custom-chart-wrapper',
        'VIDEO',
    ];
    // 通过上面的任意一个选择器，都会认为不是纯文本（取反代表是纯文本）
    return !filter.some(item => (container?.querySelectorAll(`${item}`)?.length ?? 0) > 0);
};

export const closeTag = (str: string) => {
    const list = str.match(REGEXP_HTML_G) || [];

    for (const item of list) {
        // eslint-disable-next-line array-bracket-newline, array-element-newline
        const [dom, tag] = item.match(REGEXP_HTML) || [];
        if (dom && tag && !dom.endsWith(`</${tag}>`) && !dom.endsWith('/>')) {
            const t = dom.replace('>', '/>');
            str = str.replace(dom, t);
        }
    }

    return str;
};

export const scrollToElement = (element: HTMLElement | null, offset = 0) => {
    if (!element) {
        return;
    }
    let offsetTop = 0;
    while (element) {
        offsetTop += element.offsetTop;
        element = element.offsetParent as HTMLElement;
    }

    const scroller = __INSTANCE__.config?.$scroller || window;
    scroller.scrollTo(0, offsetTop - offset);
};

/**
 * @description 判断document是否在底部
 */
export const isDocumentAtBottom = () => {
    // 获取页面总高度
    const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
    // 获取当前滚动条位置
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    // 获取视口高度
    const clientHeight = document.documentElement.clientHeight || document.body.clientHeight;
    // 判断滚动条位置加上视口高度是否大于等于页面总高度
    return Math.round(scrollTop + clientHeight) >= scrollHeight;
};

interface DomHeightParams {
    $container: HTMLElement;
    itemData?: any;
    updateHeight?: boolean;
    answerFinish?: boolean;
    isLast?: boolean;
}
export class DomHeightRecoder {
    private _domHeightCache: Record<string, number> = {};
    // 特殊标记前缀
    static PREFIX = '__MSG_HEIGHT__';
    // 需要减去高度的标记
    static EXCESS_HEIGHT = '__EXCESS_HEIGHT__';
    // 获取和设置的目标div
    static TARGET_DOM = '__TARGET_HEIGHT_DOM__';
    // 存储高度缓存的key
    static DOM_HEIGHT_CACHE = '__MSG_DOM_HEIGHT_CACHE__';
    // 清除dom高度缓存的key
    static CLEAR_DOM_HEIGHT = 'clearDomHeight';
    // dom设置高度成功的标记-方便后续的更新操作
    static SET_HEIGHT_SUCCEED = '__MSG_SET_HEIGHT_SUCCEED__';

    static getDomId(messageId: string) {
        return `${DomHeightRecoder.PREFIX}${messageId}`;
    }

    constructor() {
        this.initDomHeightCache();
    }
    initDomHeightCache() {
        const cache = localStorage.getItem(DomHeightRecoder.DOM_HEIGHT_CACHE);
        if (cache) {
            try {
                this._domHeightCache = JSON.parse(cache);
            } catch (error) {
                console.warn('解析缓存失败', error);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    @debounce(1000)
    updateDomHeightCache() {
        this.checkStorageSize();
        localStorage.setItem(DomHeightRecoder.DOM_HEIGHT_CACHE, JSON.stringify(this._domHeightCache));
    }
    checkStorageSize() {
        const domHeightCacheStr = JSON.stringify(this._domHeightCache) || '';
        const size = (domHeightCacheStr?.length * 2) / 1024 / 1024;
        if (size > 4) {
            const keys = Object.keys(this._domHeightCache);
            const halfIndex = Math.ceil(keys.length / 2);
            const lastHalf: Record<string, number> = {};
            for (let i = halfIndex; i < keys.length; i++) {
                lastHalf[keys[i]] = this._domHeightCache[keys[i]];
            }
            this._domHeightCache = lastHalf;
        }
    }
    // 过滤掉不需要的dom
    getClassName(itemData: any) {
        if (!itemData?.id || !itemData?.type) {
            return '';
        }
        const type = itemData?.type || '';
        const typeList = [
            'ask',
            'answer',
            'welcome',
        ];
        if (typeList.includes(type)) {
            return DomHeightRecoder.getDomId(itemData?.id);
        }
        return '';
    }
    // 设置特殊标记的dom高度
    setDomHeight(params: DomHeightParams) {
        try {
            const {$container, itemData} = params;
            if (!$container || !itemData || !itemData?.messageId || !itemData?.isHistory) {
                return;
            }
            const domId = DomHeightRecoder.getDomId(itemData?.messageId);
            if ($container?.className?.includes(domId)) {
                const height = this._domHeightCache?.[domId];
                if (height) {
                    $container.style.minHeight = `${height}px`;
                    // $container.style.backgroundColor = 'pink';
                }
                setTimeout(() => {
                    const targetDom = $container?.querySelector(`.${DomHeightRecoder.TARGET_DOM}`) as HTMLElement;
                    if (height && targetDom) {
                        targetDom.classList.add(DomHeightRecoder.SET_HEIGHT_SUCCEED);
                        // targetDom.style.backgroundColor = 'red';
                    }
                });
            }
        } catch (error) {}
    }

    // 储存特殊标记dom高度
    saveDomHeight(params: DomHeightParams) {
        try {
            const {$container, itemData, answerFinish = false, isLast = false, updateHeight = false} = params;
            const domId = DomHeightRecoder.getDomId(itemData?.messageId);
            if (
                !$container
                || !itemData
                || !itemData?.finish
                || !itemData?.messageId
                // 过滤一下恢复继续生成的场景
                || itemData?.isRecover
                // 过滤一下，已经储存过的历史数据，防止重复储存
                || ((itemData?.rendered || !updateHeight) && !isLast && this._domHeightCache?.[domId])
                // 过滤拍照搜题，切换过答案的场景
                || itemData?.isChangeQuestion
                // 新上屏的数据
                || (isLast && itemData?.role === 1 && !answerFinish)
                // 过滤一下mock的数据和地图数据
                || isMockId(itemData?.messageId)
                || itemData?.pluginRsp?.mapPluginInfo?.templateType === 'c-routes-map'
            ) {
                return;
            }
            const targetDom = $container?.querySelector(`.${DomHeightRecoder.TARGET_DOM}`) as HTMLElement;
            if ($container?.className?.includes(domId) && targetDom) {
                let height = targetDom?.offsetHeight || 0;
                // 查找是否含有特殊标记的div，如果有则减去高度
                const excessDivList = targetDom?.querySelectorAll(`.${DomHeightRecoder.EXCESS_HEIGHT}`) || [];
                excessDivList.forEach(($el: any) => {
                    const style = window.getComputedStyle($el);
                    if (style.display !== 'none') {
                        const marginTop = parseInt(style.marginTop, 10); // 获取 margin-top 的数值
                        const marginBottom = parseInt(style.marginBottom, 10); // 获取 margin-bottom 的数值
                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                        const subHeight = $el.offsetHeight + marginTop + marginBottom; // 计算总高度
                        height = height - subHeight;
                    }
                });
                if (height > 0) {
                    this._domHeightCache[domId] = height;
                    this.updateDomHeightCache();
                }
            }
        } catch (error) {}
    }
    // 清除特殊标记dom高度
    clearDomHeight(params: DomHeightParams) {
        try {
            const {$container, itemData, updateHeight = false} = params;
            if (!$container || !itemData || !itemData?.messageId) {
                return;
            }
            const domId = DomHeightRecoder.getDomId(itemData?.messageId);
            // const targetDom  = $container?.querySelector(`.${DomHeightRecoder.TARGET_DOM}`) as HTMLElement;
            if ($container?.className?.includes(domId)) {
                if (!updateHeight) {
                    // 需要清除当前设置的高度
                    $container.style.minHeight = 'auto';
                    // $container.style.backgroundColor = '';
                }
                // 复用错误清除地图的dom高度
                if (itemData?.pluginRsp?.mapPluginInfo?.templateType === 'c-routes-map') {
                    delete this._domHeightCache[domId];
                    this.updateDomHeightCache();
                } else if (updateHeight) {
                    // 重新设置高度
                    this.saveDomHeight({
                        ...params,
                        updateHeight,
                    });
                }
            }
        } catch (error) {}
    }

    // 删除所有dom高度的本地缓存
    clearAllDomHeight() {
        this._domHeightCache = {};
        this.updateDomHeightCache();
    }

    // 监听字体大小变化
    onFontSizeChange(fontSize: string) {
        // 监听字体大小变化 清除本地dom高度缓存。由于进入页面自动触发一次，所以这里需要判断一下
        const localFontSize = localStorage.getItem('localFontSize') || '';
        if (localFontSize !== fontSize) {
            __INSTANCE__.emitter?.emit('updateFontSize');
            localStorage.setItem('localFontSize', fontSize.toString());
            this.clearAllDomHeight();
        }
    }
}

export const domHeight = new DomHeightRecoder();
